/**
 * pages/Home.tsx
 * Accounting dashboard per active store with sales, expenses, gateways, ledger, and monthly management.
 * Multi-store support with StoreBar. Role-aware UI. Adds logout and access guard.
 * Updated:
 *  - Viewer sees all sections and icons (no hiding), but actions remain read-only/disabled.
 *  - GatewaysManager is visible to viewer with an overlay to prevent interaction.
 */

import React from 'react'
import { DashboardCards } from '../components/accounting/DashboardCards'
import { TransactionForm } from '../components/accounting/TransactionForm'
import { ExpenseForm } from '../components/accounting/ExpenseForm'
import { TransactionTable } from '../components/accounting/TransactionTable'
import { useDepositNotifications } from '../hooks/useDepositNotifications'
import { formatCurrency, formatDate } from '../lib/format'
import { CalendarClock, ArrowDownCircle, Cog, ArrowUpCircle, LogOut } from 'lucide-react'
import { ExpenseTable } from '../components/accounting/ExpenseTable'
import { GatewaysManager } from '../components/accounting/GatewaysManager'
import { ReportLedger } from '../components/accounting/ReportLedger'
import { useBoolPref } from '../hooks/usePref'
import { MonthBar } from '../components/month/MonthBar'
import { StoreBar } from '../components/store/StoreBar'
import { useAuthStore } from '../store/auth'
import { FooterSignature } from '../components/common/FooterSignature'
import { CloudSyncWidget } from '../components/cloud/CloudSyncWidget'
import { useUserStore } from '@/store/user'

/**
 * Home
 * Main page: RTL layout, multi-store switcher, KPIs, input forms, upcoming list,
 * gateways, tables, ledger, and monthly closing.
 * Viewer policy: do not hide anything; show all UI but keep modification actions disabled elsewhere.
 */
export default function Home() {

  useDepositNotifications()
  const transactions = useUserStore((s) => s.transactions)
  const expenses = useUserStore((s) => s.expenses)

  // Active store identity
  const activeStoreId = useUserStore((s) => s.activeStoreId)

  const meta = useUserStore((s) => s.getStoreById(activeStoreId!))
  const logoSrc =
    meta?.logoUrl ||
    'https://pub-cdn.sider.ai/u/U05XH97AEWZ/web-coder/68b268ce38697d89a13f32be/resource/02c6aac9-1939-41bc-9393-bb8c66a4ed5c.jpg'

  // Preference: show/hide gateways manager
  const [showGateways, setShowGateways] = useBoolPref('showGatewaysManager', true)

  // Month filter propagated to tables
  const [monthRange, setMonthRange] = React.useState<{ start: Date; end: Date } | null>(null)

  // Auth role and user
  const username = useUserStore((s) => s.username)
  const logout = useUserStore((s) => s.logout)

  //const logout = useAuthStore((s) => s.logout)


  const role = useUserStore((s) => s.role)
  const isAdmin = role === 'admin'
  const isViewer = role === 'viewer'

  const canAccessActive = React.useMemo(() => {
    if (isAdmin) return true
    return activeStoreId ? true : false
  }, [isAdmin, activeStoreId])

  const upcomingDeposits = transactions
    .filter((t) => !t.deposited)
    .sort((a, b) => (a.deposit_date > b.deposit_date ? 1 : -1))
    .slice(0, 8)

  const upcomingExpenses = expenses
    .filter((e) => !e.paid)
    .sort((a, b) => (a.payment_date > b.payment_date ? 1 : -1))
    .slice(0, 8)

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src={logoSrc} alt={meta?.name || 'Store'} className="h-10 w-10 rounded-lg object-cover" />
              <div>
                <h1 className="text-2xl font-bold">لوحة محاسبة: {meta?.name || 'المتجر'}</h1>
                <p className="mt-1 text-sm text-slate-600">
                  تتبّع المبيعات، المصروفات، تاريخ الإيداع/الدفع، ورصيد الحساب الجاري مع تنبيهات تلقائية. يحفظ النظام
                  البيانات تلقائياً ويعمل دون إنترنت. هذه البيانات خاصة بالمتجر المحدد.
                </p>
              </div>
            </div>

            {/* User actions */}
            <div className="flex items-center gap-3">
              {username && (
                <span className="text-sm text-slate-600">
                  {username} • {isAdmin ? 'مسؤول كامل' : isViewer ? 'مشاهدة فقط' : 'مستخدم إدخال'}
                </span>
              )}
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                title="تسجيل الخروج"
              >
                <LogOut className="h-4 w-4" /> خروج
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        {/* Stores bar */}
        <section className="mb-6">
          <StoreBar />
        </section>

        {/* Guard: if non-admin user has no access to active store, show message and stop */}
        {!isAdmin && !canAccessActive ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
            لا توجد صلاحية للوصول إلى أي متجر. يرجى التواصل مع المسؤول لمنحك الوصول إلى المتاجر المناسبة.
          </div>
        ) : (
          <>
            {/* Monthly selector & closing */}
            <section>
              <MonthBar onFilterChange={setMonthRange} />
            </section>

            {/* KPIs now visible for all roles */}
            <section className="mt-6">
              <DashboardCards />
            </section>

            <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                {/* Forms: visible for all; viewer will be read-only within the form components */}
                <>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h2 className="mb-3 text-lg font-semibold">إضافة عملية بيع</h2>
                    <TransactionForm />
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h2 className="mb-3 text-lg font-semibold">إضافة مصروف</h2>
                    <ExpenseForm />
                  </div>
                </>
              </div>

              <div className="space-y-6 lg:col-span-1">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-sky-600" />
                    <h2 className="text-lg font-semibold">الإيداعات القادمة</h2>
                  </div>
                  {upcomingDeposits.length === 0 ? (
                    <p className="text-sm text-slate-500">لا توجد إيداعات قادمة حالياً.</p>
                  ) : (
                    <ul className="space-y-3">
                      {upcomingDeposits.map((t) => (
                        <li
                          key={t.id}
                          className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{formatCurrency(t.net_amount)}</p>
                            <p className="text-xs text-slate-500">
                              {t.gateway} • {t.description || 'عملية بدون وصف'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <ArrowDownCircle className="h-4 w-4 text-emerald-600" />
                            <span className="text-sm">{formatDate(t.deposit_date)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-rose-600" />
                    <h2 className="text-lg font-semibold">المصروفات القادمة</h2>
                  </div>
                  {upcomingExpenses.length === 0 ? (
                    <p className="text-sm text-slate-500">لا توجد مصروفات قادمة حالياً.</p>
                  ) : (
                    <ul className="space-y-3">
                      {upcomingExpenses.map((e) => (
                        <li
                          key={e.id}
                          className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{formatCurrency(e.amount)}</p>
                            <p className="text-xs text-slate-500">
                              {e.category}
                              {e.payment_method ? ` • ${e.payment_method}` : ''} • {e.description || 'مصروف بدون وصف'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <ArrowUpCircle className="h-4 w-4 text-rose-600" />
                            <span className="text-sm">{formatDate(e.payment_date)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Gateways management: visible to all; viewer gets interaction blocked (no icons hidden) */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cog className="h-5 w-5 text-slate-700" />
                      <h2 className="text-lg font-semibold">إدارة بوابات الدفع</h2>
                    </div>
                    <button
                      onClick={() => setShowGateways((v) => !v)}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      title={showGateways ? 'إخفاء إدارة البوابات' : 'إظهار إدارة البوابات'}
                    >
                      {showGateways ? 'إخفاء' : 'إظهار'}
                    </button>
                  </div>
                  {showGateways ? (
                    <div className="relative">
                      {/* Transparent overlay for viewer to prevent interactions without hiding UI */}
                      {isViewer && <div className="absolute inset-0 z-10" aria-hidden="true" />}
                      <GatewaysManager />
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">تم إخفاء إدارة البوابات. يمكنك إظهارها من الزر أعلاه.</p>
                  )}
                </div>
              </div>
            </section>

            <section className="mt-6">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="mb-3 text-lg font-semibold">العمليات</h2>
                <TransactionTable monthRange={monthRange || undefined} />
              </div>
            </section>

            <section className="mt-6">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="mb-3 text-lg font-semibold">المصروفات</h2>
                <ExpenseTable monthRange={monthRange || undefined} />
              </div>
            </section>

            {/* Report for admins and viewers */}
            <section className="mt-6">
              <ReportLedger />
            </section>

            {/* Admin-only user management */}
            {isAdmin && (
              <section className="mt-6">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h2 className="mb-3 text-lg font-semibold">المستخدمون والصلاحيات</h2>
                  {/* Lazy import pattern skipped due to environment; direct render */}
                  {React.createElement(require('../components/auth/UserManager').UserManager)}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <FooterSignature />
    </div>
  )
}
